import { Middleware } from 'redux';
import { logoutApp } from '../../actions/login.action';
import * as uiActions from '../../actions/ui';
import { jumpContext } from '../../actions/contexts.action';
import {
  commonActions,
  cmxURL,
  authenticationChannel,
  commonEnums,
  cmxTypeguards,
  Utils,
  windowTeardownHandler,
} from '@codametrix/ui-common';
import { loggerFactory } from '@codametrix/client-logging';
import {
  RUNTIME_OVERRIDE_KEY,
  getOverrideBagForPropagation,
} from '@codametrix/client-runtime';

const logger = loggerFactory('WindowOpenMiddleware');
import { isNewDesign } from './new-design-middleware';
import { codeBenchActions } from '@codametrix/ui-common';
import { roleShim } from '../../actions/_action-utilities';
import { MiddlewareProcessor, middleWareWrapper } from './middleware-utils';

const { UserInterface } = commonActions;
const { loadRoles } = codeBenchActions;

const openWindows: Window[] = [];
const cleanUpActions = new Set([
  jumpContext.pending.type,
  'AUTH_LOGOUT/pending',
  `${uiActions.logout}`
]);

const DEFAULT_WINDOW_ARGS = {
  windowTitle: '_blank'
};

const cleanupWindows = () => {
  while (openWindows.length) {
    const handle = openWindows.pop();
    try {
      if (handle) {
        handle.close();
      }
    } catch (e) {
      logger.error(`issue closing window`);
    }
  }
};

const newDesignRuntimeOverride = '{"newAdminDesign":"true"}';

/**
 * Middleware that handles opening child windows and managing their lifecycle.
 *
 * - Listens for the `UserInterface.OPEN_CHILD_WINDOW` action and opens a new window with the provided URL and options.
 * - Appends runtime overrides and authentication channel information to the child window URL.
 * - Ensures the child window is marked as "managed" if the action payload specifies it.
 * - Dispatches the `loadRoles` action when the `UserInterface.REPLACE_UI_STATE` action is received, to update the user's roles.
 */
const windowOpenProcessor: MiddlewareProcessor = (api, next, action) => {
  if (!cmxTypeguards.isReduxAction(action)) {
    return next(action);
  }

  const shouldCleanup = cleanUpActions.has(action.type);

  // open the window and keep
  if (action.type === UserInterface.OPEN_CHILD_WINDOW) {
    const windowOpts = { ...DEFAULT_WINDOW_ARGS, ...action.payload };
    // Guarantee the parent's persisted channel name matches the name handed to
    // the child below. Otherwise a parent reload falls back to sessionStorage,
    // finds it missing/stale, mints a new channel, and lands on a different
    // BroadcastChannel than its child — silently dropping CSRF/token-rotation
    // broadcasts between the windows.
    sessionStorage.setItem('channel', authenticationChannel.name);
    // construct URL
    const loc = `${window.location.origin}`;
    const searchParams = new URLSearchParams({
      ...action.payload.params,
      channel: authenticationChannel.name
    });
    searchParams.set(
      commonEnums.AuthenticationChannelEvents.BOOTSTRAP_AUTH,
      `1`
    );

    // if runtime overrides are present pass them along to child
    // windows as well.
    const overrideBag = getOverrideBagForPropagation();
    if (Object.keys(overrideBag).length !== 0) {
      searchParams.set(RUNTIME_OVERRIDE_KEY, JSON.stringify(overrideBag));
    }

    // Temporary fix for new design because
    // some popups do not include a component-based redesign
    if (isNewDesign) {
      searchParams.append(RUNTIME_OVERRIDE_KEY, newDesignRuntimeOverride);
    }
    // CQA-UI append v2/ in case of new UI flow
    const isNewFlow = Utils.doesPathEndWithV2();
    const openLocation = `${action.payload.url}${
      isNewFlow ? 'v2/' : ''
    }?${searchParams}`;
    const url = new URL(openLocation, loc);
    const formattedUrl = cmxURL.pathBuilder(url);

    // construct window title.  each auth channel will have it's own set.
    // Commenting below to enable single child window functionality
    const windowTitle = `${windowOpts.windowTitle}_${
      authenticationChannel.name
    }_${searchParams.get('windowNameExtension') ?? ''}`;

    const handle = window.open(
      formattedUrl,
      windowTitle,
      windowOpts.windowFeatures
    );

    // Ensure any pop-up window opens up is managed
    const managedString = `${windowOpts?.params?.managed ?? false}`;
    const managed = managedString === 'true';

    if (handle !== null && managed) {
      openWindows.push(handle);
    }
  } else if (action.type === UserInterface.REPLACE_UI_STATE) {
    const roles = roleShim(action?.payload?.context, []);
    if (roles?.length > 0) {
      next(loadRoles(roles));
    }
  } else if (shouldCleanup) {
    cleanupWindows();
  }

  return next(action);
};
export const WindowOpenMiddleware: Middleware = middleWareWrapper(
  windowOpenProcessor
);
// Use pagehide (via the teardown helper) instead of the deprecated `unload` event,
// which modern browsers block under Permissions-Policy and never fire.
windowTeardownHandler.addEventListener(cleanupWindows);

export default WindowOpenMiddleware;
